import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { differenceInHours } from "date-fns";
import { addPeriod, toDateString } from "helpers";
import { initializeJobVisit } from "helpers/initializeJobVisit";
import { isEqual } from "lodash";
import { Jobs } from "models/Job";
import {
  GetChecklistGroupsQueryVariables,
  GetMachinesQueryVariables,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State, storeVisitsToLocal } from "./jobs.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getJob: createAppAsyncThunk(
    "jobs/getJob",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { jobs } = getState();
      const selectedJobId = jobs.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getJob, {
        variables: {
          id: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job) return rejectWithValue("something went wrong");
      return {
        data,
        selectedJobId,
      };
    },
  ),
  getJobs: createAppAsyncThunk(
    "jobs/getJobs",
    async (props: { force?: boolean }, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        jobs: { lastLoaded, jobFilter },
      } = getState();
      if (!props.force && lastLoaded && differenceInHours(new Date(lastLoaded), new Date()) < 1) {
        return rejectWithValue("Abort");
      }
      let selectedSymptoms = jobFilter.selectedSymptoms.map((x) => x?.code) as string[];
      selectedSymptoms = jobFilter.selectedSymptoms.map((x) => x?.description) as string[];

      const { data, errors } = await graphqlRequest(sdk.getJobs, {
        variables: {
          max: 100,
          jobFilter: {
            jobId: jobFilter.jobId,
            toDate: toDateString(addPeriod(jobFilter.typeDate)),
            plannedDate: toDateString(jobFilter.specificDate),
            symptomIds: selectedSymptoms,
            areaCode: jobFilter.areaCode,
            customerId: jobFilter.customer?.id,
            city: jobFilter.city,
            postalCode: jobFilter.postalCode,
          },
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.jobs) return rejectWithValue("something went wrong");
      return { data };
    },
  ),
  refreshJob: createAppAsyncThunk(
    "jobs/refreshJob",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { jobs } = getState();
      const selectedJobId = jobs.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.refreshJob, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job || !data?.files || !data?.workNotes)
        return rejectWithValue("something went wrong");
      return {
        data,
        selectedJobId,
      };
    },
  ),
  getCustomers: createAppAsyncThunk(
    "jobs/getCustomers",
    async (variables: { nameFilter: string }, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomersSimple, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.customers) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  getFiles: createAppAsyncThunk(
    "jobs/getFiles",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { jobs } = getState();
      const selectedJobId = jobs.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getFiles, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.files) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getNotes: createAppAsyncThunk(
    "jobs/getNotes",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { jobs } = getState();
      const selectedJobId = jobs.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getWorkNotes, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.workNotes) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  getComments: createAppAsyncThunk(
    "jobs/getComments",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      let { jobs } = getState();
      const selectedJobId = jobs.selectedJobId;
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getComments, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.comments) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    },
  ),
  //ChangeJobMachineDialog
  getMachines: createAppAsyncThunk(
    "jobs/getMachines",
    async (variables: GetMachinesQueryVariables, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getMachines, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.machines) return rejectWithValue("something went wrong");
      return data;
    },
  ),
  //Contacts
  getContacts: createAppAsyncThunk(
    "jobs/getContacts",
    async (_, { rejectWithValue, getState, extra: { sdk } }) => {
      const {
        jobs: { jobs, selectedJobId },
      } = getState();
      if (!selectedJobId) return rejectWithValue("No selected job");
      const { customer } = jobs[selectedJobId];
      if (!customer || !customer.id) return rejectWithValue("No customer"); // Y doe????
      const { data, errors } = await graphqlRequest(sdk.getContacts, {
        variables: {
          customerId: customer?.id,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.contacts) return rejectWithValue("something went wrong");
      return data.contacts;
    },
  ),
  //AddChecklistDialog
  getChecklistGroups: createAppAsyncThunk(
    "jobs/getChecklistGroups",
    async (variables: GetChecklistGroupsQueryVariables, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getChecklistGroups, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.checklistGroups) return rejectWithValue("something went wrong");
      return data;
    },
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.getJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(asyncQueries.getJob.fulfilled, (state, { payload: { data, selectedJobId } }) => {
    let { job } = data;
    if (!selectedJobId || !job) return state;
    /**
     * While this "if" shouldn't be necessary, selected job can be undefined
     * if you start the app or reload while viewing a job
     */
    if (!state.jobs[selectedJobId]) {
      state.jobs[selectedJobId] = {
        ...job,
        files: [],
        workNotes: [],
        visits: [],
        comments: [],
      };
    } else {
      state.jobs[selectedJobId] = {
        ...state.jobs[selectedJobId],
        ...job,
        files: state.jobs[selectedJobId].files || [],
        workNotes: state.jobs[selectedJobId].workNotes || [],
        visits: state.jobs[selectedJobId].visits || [],
        comments: state.jobs[selectedJobId].comments || [],
      };
    }
    if (!state.jobVisits[job.id]) {
      initializeJobVisit(state, state.jobs[selectedJobId]);
    }
    state.loadingJob = false;
    return state;
  });
  builder.addCase(asyncQueries.refreshJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.refreshJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(
    asyncQueries.refreshJob.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      let { job } = data;
      if (!selectedJobId || !job || !state.jobs[selectedJobId]) return state;
      const oldFiles = state.jobs[selectedJobId].files;
      const oldPreorderedParts = state.jobs[selectedJobId].preOrderedParts;
      const oldSymptomText = state.jobs[selectedJobId].symptomDescription;
      const oldInternalNotes = state.jobs[selectedJobId].workNotes;
      state.jobs[selectedJobId] = {
        ...state.jobs[selectedJobId],
        ...job,
        files: [...data.files],
        workNotes: [...data.workNotes],
        visits: state.jobs[selectedJobId].visits || [],
        comments: state.jobs[selectedJobId].comments || [],
      };
      state.refreshJobNotifications.symptomText = !isEqual(
        oldSymptomText,
        data.job?.symptomDescription,
      );
      state.refreshJobNotifications.internalNotes =
        data.workNotes.length !== oldInternalNotes.length ||
        !isEqual(oldInternalNotes, data.workNotes);
      state.refreshJobNotifications.files =
        data.files.length !== oldFiles.length || !isEqual(oldFiles, data.files);
      state.refreshJobNotifications.preOrderedParts =
        data.job?.preOrderedParts.length !== oldPreorderedParts.length ||
        !isEqual(oldPreorderedParts, data.job.preOrderedParts);

      if (!state.jobVisits[job.id]) {
        initializeJobVisit(state, state.jobs[selectedJobId]);
      }
      storeVisitsToLocal(state);
      state.loadingJob = false;
      return state;
    },
  );
  builder.addCase(asyncQueries.getJobs.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.loadingJobs = true;
    return state;
  });
  builder.addCase(asyncQueries.getJobs.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.loadingJobs = false;
    return state;
  });
  builder.addCase(asyncQueries.getJobs.fulfilled, (state, { payload: { data } }) => {
    let oldJobs = state.jobs;
    let oldJobIds = Object.keys(oldJobs);
    let serviceJobs = data.jobs;
    let jobVisits = state.jobVisits;
    if (serviceJobs.length > 0) {
      let newJobs = serviceJobs.reduce((obj, next) => {
        if (!oldJobs[next.id]) {
          obj[next.id] = {
            ...next,
            files: [],
            workNotes: [],
            visits: [],
            comments: [],
          };
        } else {
          obj[next.id] = {
            ...oldJobs[next.id],
            ...next,
            files: oldJobs[next.id].files || [],
            workNotes: oldJobs[next.id].workNotes || [],
            visits: oldJobs[next.id].visits || [],
            comments: oldJobs[next.id].comments || [],
          };
        }

        const job = obj[next.id];
        if (!jobVisits[job.id]) {
          initializeJobVisit(state, job);
        }
        return obj;
      }, {} as Jobs);

      if (state.hideNewJobNotification) {
        state.newJobIds = [];
      } else {
        let newJobIds = Object.keys(newJobs);
        state.newJobIds = oldJobIds.length ? newJobIds.filter((x) => !oldJobIds.includes(x)) : [];
      }
      state.hideNewJobNotification = false;

      state.jobs = newJobs;
    } else {
      state.jobs = {};
    }
    state.loadingJobs = false;
    state.lastLoaded = new Date().toISOString();
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.pending, (state) => {
    state.filterOptions.loadingCustomers = true;
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.rejected, (state) => {
    state.filterOptions.loadingCustomers = false;
    return state;
  });
  builder.addCase(asyncQueries.getCustomers.fulfilled, (state, { payload: { customers } }) => {
    state.filterOptions.loadingCustomers = false;
    state.filterOptions.customers = [...customers];
    return state;
  });
  builder.addCase(
    asyncQueries.getFiles.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].files = [...data.files];
      return state;
    },
  );
  builder.addCase(
    asyncQueries.getNotes.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].workNotes = [...data.workNotes];
      return state;
    },
  );
  // Comments
  builder.addCase(asyncQueries.getComments.pending, (state) => {
    state.commentsLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getComments.rejected, (state) => {
    state.commentsLoading = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getComments.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.commentsLoading = false;
      state.jobs[selectedJobId].comments = [...data.comments];
      return state;
    },
  );
  // ChangeJobMachineDialog
  builder.addCase(asyncQueries.getMachines.pending, (state) => {
    state.changeJobMachine.loadingMachines = true;
    return state;
  });
  builder.addCase(asyncQueries.getMachines.rejected, (state) => {
    state.changeJobMachine.loadingMachines = false;
    return state;
  });
  builder.addCase(asyncQueries.getMachines.fulfilled, (state, { payload: { machines } }) => {
    state.changeJobMachine.loadingMachines = false;
    state.changeJobMachine.machines = [...machines];
    return state;
  });
  // Contacts
  builder.addCase(asyncQueries.getContacts.rejected, (state, { payload: errors }) => {
    state.contactsLoading = false;
    return state;
  });
  builder.addCase(asyncQueries.getContacts.pending, (state) => {
    state.contactsLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getContacts.fulfilled, (state) => {
    state.contactsLoading = false;
    return state;
  });
  // AddChecklistDialog
  builder.addCase(asyncQueries.getChecklistGroups.pending, (state) => {
    state.addChecklist.loading = true;
    return state;
  });
  builder.addCase(asyncQueries.getChecklistGroups.rejected, (state) => {
    state.addChecklist.loading = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getChecklistGroups.fulfilled,
    (state, { payload: { checklistGroups } }) => {
      state.addChecklist.loading = false;
      state.addChecklist.checklists = [...checklistGroups];
      return state;
    },
  );
};
